"use client";
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';


export const dynamic = 'force-dynamic';

export default function Pricing() {
  const { t } = useLanguage();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const packs = [
    {
      name: 'Pack 1',
      title: t('pricing.pack1_title'),
      subtitle: t('pricing.pack1_subtitle'),
      price: '19.90',
      setup: '890',
      period: t('pricing.month'),
      color: 'card-glass',
      featured: false,
      features: [
        { label: t('pricing.pack1_f1'), included: true },
        { label: t('pricing.pack1_f2'), included: true },
        { label: t('pricing.pack1_f3'), included: true },
        { label: t('pricing.pack1_f4'), included: true },
        { label: t('pricing.pack1_f5'), included: true },
        { label: t('pricing.pack1_f6'), included: true },
        { label: t('pricing.pack1_f7'), included: true },
        { label: t('pricing.pack1_f8'), included: false },
        { label: t('pricing.pack1_f9'), included: false },
        { label: t('pricing.pack1_f10'), included: false },
        { label: t('pricing.pack1_f11'), included: false },
        { label: t('pricing.pack1_f12'), included: false },
      ],
      cta: t('pricing.pack1_cta'),
      ideal: t('pricing.pack1_ideal'),
    },
    {
      name: 'Pack 2',
      title: t('pricing.pack2_title'),
      subtitle: t('pricing.pack2_subtitle'),
      price: '39.90',
      setup: '890',
      period: t('pricing.month'),
      color: 'pricing-featured',
      featured: true,
      features: [
        { label: t('pricing.pack2_f1'), included: true },
        { label: t('pricing.pack2_f2'), included: true },
        { label: t('pricing.pack2_f3'), included: true },
        { label: t('pricing.pack2_f4'), included: true },
        { label: t('pricing.pack2_f5'), included: true },
        { label: t('pricing.pack2_f6'), included: true },
        { label: t('pricing.pack2_f7'), included: true },
        { label: t('pricing.pack2_f8'), included: true },
        { label: t('pricing.pack2_f9'), included: true },
        { label: t('pricing.pack2_f10'), included: true },
        { label: t('pricing.pack2_f11'), included: true },
        { label: t('pricing.pack2_f12'), included: false },
      ],
      cta: t('pricing.pack2_cta'),
      ideal: t('pricing.pack2_ideal'),
    },
    {
      name: 'Pack 3',
      title: t('pricing.pack3_title'),
      subtitle: t('pricing.pack3_subtitle'),
      price: '99.90',
      setup: '890',
      period: t('pricing.month'),
      color: 'card-glass border border-purple-500/20',
      featured: false,
      features: [
        { label: t('pricing.pack3_f1'), included: true },
        { label: t('pricing.pack3_f2'), included: true },
        { label: t('pricing.pack3_f3'), included: true },
        { label: t('pricing.pack3_f4'), included: true },
        { label: t('pricing.pack3_f5'), included: true },
        { label: t('pricing.pack3_f6'), included: true },
        { label: t('pricing.pack3_f7'), included: true },
        { label: t('pricing.pack3_f8'), included: true },
        { label: t('pricing.pack3_f9'), included: true },
        { label: t('pricing.pack3_f10'), included: true },
        { label: t('pricing.pack3_f11'), included: true },
        { label: t('pricing.pack3_f12'), included: true },
      ],
      cta: t('pricing.pack3_cta'),
      ideal: t('pricing.pack3_ideal'),
    },
  ];

  const faqs = [
    { q: t('pricing.faq1_q'), a: t('pricing.faq1_a') },
    { q: t('pricing.faq2_q'), a: t('pricing.faq2_a') },
    { q: t('pricing.faq3_q'), a: t('pricing.faq3_a') },
    { q: t('pricing.faq4_q'), a: t('pricing.faq4_a') },
    { q: t('pricing.faq5_q'), a: t('pricing.faq5_a') },
    { q: t('pricing.faq6_q'), a: t('pricing.faq6_a') },
  ];

  const setupFeatures = [
    t('pricing.setup_f1'),
    t('pricing.setup_f2'),
    t('pricing.setup_f3'),
    t('pricing.setup_f4'),
    t('pricing.setup_f5'),
    t('pricing.setup_f6'),
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('revealed')),
      { threshold: 0.15 }
    );
    document.querySelectorAll('.reveal-hidden')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-bg text-fg overflow-x-hidden">
      <div className="noise-overlay" />
      <Header />
      {/* Lightweight Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="blob bg-accent2/15 w-[400px] h-[400px] top-0 right-0" />
        <div className="grid-bg absolute inset-0 opacity-50" />
        <div className="max-w-8xl mx-auto px-6 lg:px-8 relative z-10 text-center">
          <span className="badge-accent mb-4 inline-block">{t('pricing.title')} {t('pricing.title_accent')}</span>
          <h1 className="font-display font-800 text-4xl md:text-6xl leading-tight tracking-tight mb-5">
            {t('pricing.hero_title')}{' '}
            <span className="gradient-text">{t('pricing.hero_title_accent')}</span>
          </h1>
          <p className="font-body text-muted text-xl max-w-2xl mx-auto leading-relaxed">
            {t('pricing.subtitle')}
          </p>
        </div>
      </section>
      {/* Pricing Cards */}
      <section className="relative py-16">
        <div className="max-w-8xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {packs?.map((pack, i) => (
              <div
                key={pack?.name}
                className={`reveal-hidden stagger-${i + 1} relative rounded-2xl p-7 flex flex-col ${pack?.color}`}
              >
                {pack?.featured && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10">
                    <span className="bg-accent text-bg text-xs font-display font-700 px-5 py-1.5 rounded-full tracking-wide">
                      ⭐ {t('pricing.popular')}
                    </span>
                  </div>
                )}

                <div className="mb-2">
                  <span className="text-xs font-display font-700 text-accent uppercase tracking-widest">{pack?.name}</span>
                </div>
                <h2 className="font-display font-700 text-2xl text-fg mb-1">{pack?.title}</h2>
                <p className="text-sm text-muted font-body mb-6">{pack?.subtitle}</p>

                <div className="mb-2">
                  <div className="flex items-baseline gap-1">
                    <span className="text-5xl font-display font-800 gradient-text">{pack?.price}</span>
                    <span className="text-muted text-base font-body">CHF{t('pricing.month')}</span>
                  </div>
                  <p className="text-sm text-muted mt-1 font-body">+ CHF {pack?.setup}.- {t('pricing.setup')}</p>
                </div>

                <div className="divider-accent my-6" />

                <p className="text-xs text-muted font-body italic mb-6 leading-relaxed">{pack?.ideal}</p>

                <ul className="space-y-2.5 mb-8 flex-1">
                  {pack?.features?.map((f) => (
                    <li key={f?.label} className={`flex items-start gap-2.5 text-sm font-body ${f?.included ? 'text-fg/80' : 'text-muted/50'}`}>
                      {f?.included ? (
                        <svg width="14" height="14" fill="none" stroke="#00C2FF" strokeWidth="2.5" viewBox="0 0 24 24" className="mt-0.5 flex-shrink-0">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                      ) : (
                        <svg width="14" height="14" fill="none" stroke="#6B7FA3" strokeWidth="2" viewBox="0 0 24 24" className="mt-0.5 flex-shrink-0">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                      {f?.label}
                    </li>
                  ))}
                </ul>

                <Link
                  href="/contact"
                  className={`block text-center py-3.5 px-5 rounded-full text-sm font-display font-600 transition-all duration-300 ${
                    pack?.featured ? 'btn-primary relative z-10' : 'btn-secondary'
                  }`}
                >
                  {pack?.cta}
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center mt-10 reveal-hidden">
            <p className="text-muted text-sm font-body">
              {t('pricing.custom_need')}{' '}
              <Link href="/contact" className="text-accent hover:underline">{t('pricing.custom_link')}</Link>
            </p>
          </div>
        </div>
      </section>
      {/* Setup explanation */}
      <section className="relative py-16 section-bg-alt">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="reveal-hidden card-glass rounded-2xl p-8 md:p-12">
            <div className="flex items-start gap-6">
              <div className="w-14 h-14 rounded-xl bg-accent/20 flex items-center justify-center flex-shrink-0">
                <svg width="28" height="28" fill="none" stroke="#00C2FF" strokeWidth="1.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                </svg>
              </div>
              <div>
                <h3 className="font-display font-700 text-2xl mb-3">{t('pricing.setup_title')}</h3>
                <p className="text-muted font-body leading-relaxed mb-4">
                  {t('pricing.setup_desc')}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {setupFeatures?.map((item) => (
                    <div key={item} className="flex items-center gap-2 text-sm text-fg/70 font-body">
                      <svg width="14" height="14" fill="none" stroke="#00C2FF" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* FAQ */}
      <section className="relative py-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12 reveal-hidden">
            <span className="badge-accent mb-4 inline-block">{t('pricing.faq_badge')}</span>
            <h2 className="font-display font-700 text-3xl md:text-4xl tracking-tight">
              {t('pricing.faq_title')}
            </h2>
          </div>
          <div className="space-y-3">
            {faqs?.map((faq, i) => (
              <div
                key={i}
                className={`reveal-hidden stagger-${(i % 5) + 1} card-glass rounded-xl overflow-hidden transition-all duration-300`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors"
                >
                  <span className="font-display font-600 text-sm md:text-base text-fg pr-4">{faq?.q}</span>
                  <svg
                    width="18" height="18" fill="none" stroke="#00C2FF" strokeWidth="2" viewBox="0 0 24 24"
                    className={`flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5">
                    <div className="divider-accent mb-4" />
                    <p className="text-muted font-body text-sm leading-relaxed">{faq?.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="relative py-20 section-bg-alt">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center reveal-hidden">
          <h2 className="font-display font-700 text-3xl md:text-4xl tracking-tight mb-4">
            {t('pricing.cta_title')}
          </h2>
          <p className="text-muted font-body mb-8">{t('pricing.cta_sub')}</p>
          <Link href="/contact" className="btn-primary px-8 py-4 text-base inline-block relative z-10">
            {t('pricing.start_btn')}
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}